import React, { useState } from "react";

import { MarketplaceFilter } from "./MarketplaceFilter";
import { useNavigate } from "react-router-dom";

interface MarketplaceNavbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onFilterApply: (filters: Record<string, unknown>) => void;
}

export const MarketplaceNavbar: React.FC<MarketplaceNavbarProps> = ({
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
  onFilterApply,
}) => {
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(false);

  const handleFilterToggle = () => {
    // Only show filter if we're on the "all" tab
    if (activeTab === "all") {
      setShowFilter(!showFilter);
    }
  };

  const handleFilterClose = () => {
    setShowFilter(false);
  };

  const handleFilterApply = (filters: Record<string, unknown>) => {
    onFilterApply(filters);
    setShowFilter(false);
  };

  return (
    <>
      <div className="sticky top-0 z-10 bg-[#FBF6F1] py-3">
        {/* Search bar */}
        <div className="w-full px-4 py-2 inline-flex justify-start items-center gap-3">
          <div className="flex-1 px-3 py-2 bg-white rounded-md flex justify-start items-center gap-2">
            <div className="size-6 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="#1E3A3A"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Tìm kiếm sản phẩm..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-full text-[#6F6F6B] font-normal font-sora"
            />
          </div>
          <div
            className="size- p-2 flex justify-start items-center gap-2.5 cursor-pointer"
            onClick={handleFilterToggle}
          >
            <div className="size-6 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M3.99951 3H19.9996C20.5519 3 20.9996 3.44764 20.9996 3.99987L20.9998 5.58569C20.9999 5.85097 20.8945 6.10538 20.7069 6.29295L14.2924 12.7071C14.1049 12.8946 13.9995 13.149 13.9995 13.4142V19.7192C13.9995 20.3698 13.3881 20.8472 12.757 20.6894L10.757 20.1894C10.3118 20.0781 9.99951 19.6781 9.99951 19.2192V13.4142C9.99951 13.149 9.89415 12.8946 9.70662 12.7071L3.2924 6.29289C3.10486 6.10536 2.99951 5.851 2.99951 5.58579V4C2.99951 3.44772 3.44722 3 3.99951 3Z"
                  stroke="#1E3A3A"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <div
            className="size- p-2 flex justify-start items-center gap-2.5 cursor-pointer"
            onClick={() => navigate("/marketplace/cart")}
          >
            <div className="size-6 relative overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19.5 22C20.3284 22 21 21.3284 21 20.5C21 19.6716 20.3284 19 19.5 19C18.6716 19 18 19.6716 18 20.5C18 21.3284 18.6716 22 19.5 22Z"
                  fill="#1E3A3A"
                  stroke="#1E3A3A"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.5 19.75C19.0858 19.75 18.75 20.0858 18.75 20.5C18.75 20.9142 19.0858 21.25 19.5 21.25C19.9142 21.25 20.25 20.9142 20.25 20.5C20.25 20.0858 19.9142 19.75 19.5 19.75ZM17.25 20.5C17.25 19.2574 18.2574 18.25 19.5 18.25C20.7426 18.25 21.75 19.2574 21.75 20.5C21.75 21.7426 20.7426 22.75 19.5 22.75C18.2574 22.75 17.25 21.7426 17.25 20.5Z"
                  fill="#1E3A3A"
                />
                <path
                  d="M9.5 22C10.3284 22 11 21.3284 11 20.5C11 19.6716 10.3284 19 9.5 19C8.67157 19 8 19.6716 8 20.5C8 21.3284 8.67157 22 9.5 22Z"
                  fill="#1E3A3A"
                  stroke="#1E3A3A"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.5 19.75C9.08579 19.75 8.75 20.0858 8.75 20.5C8.75 20.9142 9.08579 21.25 9.5 21.25C9.91421 21.25 10.25 20.9142 10.25 20.5C10.25 20.0858 9.91421 19.75 9.5 19.75ZM7.25 20.5C7.25 19.2574 8.25736 18.25 9.5 18.25C10.7426 18.25 11.75 19.2574 11.75 20.5C11.75 21.7426 10.7426 22.75 9.5 22.75C8.25736 22.75 7.25 21.7426 7.25 20.5Z"
                  fill="#1E3A3A"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.25 2C1.25 1.58579 1.58579 1.25 2 1.25C3.96839 1.25 5.05306 2.34155 5.51124 3.25H22C22.2224 3.25 22.4333 3.3487 22.5758 3.51944C22.7183 3.69018 22.7777 3.91536 22.7379 4.13416L20.7379 15.1342C20.6731 15.4908 20.3625 15.75 20 15.75H7C6.63754 15.75 6.32694 15.4908 6.2621 15.1342L4.26716 4.16199C4.15331 3.74877 3.56536 2.75 2 2.75C1.58579 2.75 1.25 2.41421 1.25 2ZM5.89866 4.75L7.62593 14.25H19.3741L21.1013 4.75H5.89866Z"
                  fill="#1E3A3A"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.64869 16.1411C3.39118 16.3359 3.25 16.6065 3.25 17C3.25 17.3935 3.39118 17.6641 3.64869 17.8589C3.93322 18.0741 4.43492 18.25 5.23077 18.25H19.5C19.9142 18.25 20.25 18.5858 20.25 19C20.25 19.4142 19.9142 19.75 19.5 19.75H5.23077C4.24231 19.75 3.37863 19.5353 2.74385 19.0552C2.08205 18.5547 1.75 17.8253 1.75 17C1.75 16.1747 2.08205 15.4453 2.74385 14.9448C3.37863 14.4647 4.24231 14.25 5.23077 14.25H20C20.4142 14.25 20.75 14.5858 20.75 15C20.75 15.4142 20.4142 15.75 20 15.75H5.23077C4.43492 15.75 3.93322 15.9259 3.64869 16.1411Z"
                  fill="#1E3A3A"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="w-full inline-flex justify-start items-center">
          <div
            data-property-1={activeTab === "all" ? "active" : "default"}
            className={`flex-1 p-4 flex justify-center items-center gap-2.5 ${
              activeTab === "all" ? "border-b border-[#1E3A3A]" : ""
            }`}
            onClick={() => onTabChange("all")}
          >
            <div
              className={`text-center w-full text-sm font-sora leading-none ${
                activeTab === "all"
                  ? "text-[#1E3A3A] font-medium"
                  : "text-[#6F6F6B] font-normal"
              }`}
            >
              Tất cả sản phẩm
            </div>
          </div>
          <div
            data-property-1={activeTab === "yours" ? "active" : "default"}
            className={`flex-1 p-4 flex justify-center items-center gap-2.5 ${
              activeTab === "yours" ? "border-b border-[#1E3A3A]" : ""
            }`}
            onClick={() => onTabChange("yours")}
          >
            <div
              className={`text-center justify-start text-sm font-sora leading-none ${
                activeTab === "yours"
                  ? "text-[#1E3A3A] font-medium"
                  : "text-[#6F6F6B] font-normal"
              }`}
            >
              Sản phẩm của bạn
            </div>
          </div>
        </div>
      </div>

      {/* Filter modal */}
      {showFilter && (
        <MarketplaceFilter
          onClose={handleFilterClose}
          onApply={handleFilterApply}
        />
      )}
    </>
  );
};

export default MarketplaceNavbar;
